import type {
  DpsMiniAppBackendCoinDto,
  DpsMiniAppBackendOrderInfoDto,
  DpsMiniAppBackendUserDto,
} from "@gameApp/api/types/DpsMiniAppBackend";
import { IS_PRODUCTION } from "@gameApp/constants";

export interface AppStoreState {
  tokenAccess: string | null;
  tokenRefresh: string | null;
  user: DpsMiniAppBackendUserDto | null;
  miniGame: {
    ordersInfo?: DpsMiniAppBackendOrderInfoDto;
    coins?: DpsMiniAppBackendCoinDto[];
    bonusFetched?: boolean;
  };
}

const DEBUG = !IS_PRODUCTION;

const APP_STORE_STATE_INITIAL: AppStoreState = {
  tokenAccess: null,
  tokenRefresh: null,
  user: null,
  miniGame: {
    ordersInfo: undefined,
    coins: undefined,
    bonusFetched: false,
  },
};

export type AppStoreActionSubscribe = (state: AppStoreState) => void;

export type AppStoreActionDispatch = (state: AppStoreState) => AppStoreState;

class AppStore {
  private static instance: AppStore;

  private constructor(
    private store: AppStoreState = APP_STORE_STATE_INITIAL,
    private subscribers: AppStoreActionSubscribe[] = [],
    private updates = 0,
  ) {}

  public static getInstance = (): AppStore => {
    if (!AppStore.instance) {
      AppStore.instance = new AppStore();
    }

    return AppStore.instance;
  };

  public subscribe = (callback: AppStoreActionSubscribe): void => {
    if (typeof callback === "function") {
      if (DEBUG) {
        console.warn("AppStore: Subscribing");
      }

      this.subscribers.push(callback);
    } else {
      console.warn("AppStore: Callback should be a function");
    }
  };

  public unsubscribe = (callback: AppStoreActionSubscribe): void => {
    if (DEBUG) {
      console.warn("AppStore: Unsubscribing");
    }

    const foundIndex = this.subscribers.findIndex((sub) => sub === callback);

    if (foundIndex !== -1) {
      this.subscribers.splice(foundIndex, 1);
    }
  };

  private readonly notify = (): void => {
    for (const callback of this.subscribers) {
      if (DEBUG) {
        console.warn("AppStore: Notifying");
      }

      callback(this.store);
    }
  };

  public get state(): AppStoreState {
    return this.store;
  }

  public readonly dispatch = (action: AppStoreActionDispatch): void => {
    if (DEBUG) {
      console.warn("AppStore: Dispatching");
    }

    this.store = action(this.store);
    this.incrementUpdates();
    this.notify();
  };

  private readonly incrementUpdates = (): void => {
    this.updates += 1;

    if (DEBUG) {
      console.warn("AppStore: Incrementing updates", this.updates, this.store);
    }
  };
}

export const appStore = AppStore.getInstance();
